import moment, { Moment } from 'moment';
import { filter, orderBy, findIndex, get, includes, some, toLower } from 'lodash';

import { formatTimestamp } from '@src/emp-3.0/utils';
import { ESortOrder } from '@src/emp-3.0/types';
import { EDataTransformDimensionName, sortByType } from '@src/common/constant/enum';

import { DEFAULT_VALUE, deploymentValuesMap } from './constants';

interface ISearchParameter {
  searchText?: string;
}
interface IFilterParameter {
  type: string;
  updatedBy: string;
  updateDate: Moment[] | null;
}

export const filterData = (
  data: any[],
  searchParameter: ISearchParameter,
  filterParameter: IFilterParameter,
) => {
  const { searchText } = searchParameter;
  const { type, updatedBy, updateDate } = filterParameter;

  const lowerSearchText = toLower(searchText);

  return filter(data, (item) => {
    const formattedTime = formatTimestamp(item.edited_time);

    const fieldsToSearch = [
      EDataTransformDimensionName[item.name] ?? item.name,
      deploymentValuesMap[item.type] ?? item.type,
      item.last_updated_by,
      formattedTime,
    ];

    const matchesSearch = lowerSearchText
      ? some(fieldsToSearch, (field) => includes(toLower(field), lowerSearchText))
      : true;

    const matchesDeploymentType =
      type === DEFAULT_VALUE ||
      (type === 'Dimension'
        ? ['data_transform', 'custom_dimension'].includes(item.type)
        : item.type === type);

    const matchesUpdatedBy =
      updatedBy !== DEFAULT_VALUE ? item.last_updated_by === updatedBy : true;

    let matchesUpdatedDate = true;
    if (updateDate?.[0] && updateDate?.[1]) {
      if (!isNaN(item.edited_time)) {
        const time = moment(item.edited_time * 1000);
        matchesUpdatedDate = time.isBetween(updateDate[0], updateDate[1]);
      }
    }

    return matchesSearch && matchesDeploymentType && matchesUpdatedBy && matchesUpdatedDate;
  });
};

export const sortData = (data: any[], sort: { key: string; order: number }) => {
  const { key: sortKey, order: sortOrder } = sort;

  if (!sortKey || sortOrder === 0) return data;

  const order = sortOrder === ESortOrder.DESC ? [sortByType.DESC] : [sortByType.ASC];

  return orderBy(data, [(item) => get(item, sortKey)], order);
};

export const handleDefaultSelectedItem = (data: any[], defaultSelectedId?: string | null) => {
  if (!defaultSelectedId)
    return {
      data,
      selectedRowKeys: [],
      selectedRows: [],
    };

  const numberId = Number(defaultSelectedId);
  const dataIndex = findIndex(data, { id: numberId });
  const items: any[] = [];
  if (dataIndex !== -1) {
    const [item] = data.splice(dataIndex, 1);
    items.push(item);
    data.unshift(item);
  }
  return {
    data,
    selectedRowKeys: [numberId],
    selectedRows: items,
  };
};

export const filterFlowType = (data: any[]) => {
  return data.map((item, index) => ({ ...item, key: index }));
};

export const generateDeploymentTableFilterData = (
  data: any[],
  searchParameter: ISearchParameter,
  filterParameter: IFilterParameter,
  sort: { key: string; order: number },
  defaultSelectedId?: string | null,
) => {
  // search and filter
  let resultData = filterData(data, searchParameter, filterParameter);

  // sort
  resultData = sortData(resultData, sort);

  // The default selected item is brought to the top of the array.
  const {
    data: defaultSelectedData,
    selectedRowKeys,
    selectedRows,
  } = handleDefaultSelectedItem(resultData, defaultSelectedId);

  resultData = filterFlowType(defaultSelectedData);

  return {
    data: resultData,
    selectedRowKeys,
    selectedRows,
  };
};
